import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { X, Sparkles, ExternalLink } from "lucide-react";

/** Phase 2 — Inline "Ask AI" panel next to the top search bar.
 *  Returns {answer, citations[{contract_id, contract_number, vendor, snippet}]}.
 *  Restricted contracts NEVER surface in results — enforced server-side. */
export default function AISearchPanel({ open, initialQuery, onClose }) {
  const nav = useNavigate();
  const [q, setQ] = useState(initialQuery || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setQ(initialQuery || "");
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open || !initialQuery) return;
    // Auto-run when opened with an NL-looking query.
    run(initialQuery);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const run = async (query) => {
    if (!(query || "").trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/ai/search", { query: query.trim() });
      setData(r.data);
    } catch (e) {
      setData({ answer: e?.response?.data?.detail || "Search failed", citations: [] });
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-start justify-center pt-20 p-4" onClick={onClose}
         data-testid="ai-search-panel">
      <div className="bg-white rounded-md w-full max-w-3xl shadow-xl max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#005289]" />
          <div className="font-bold text-sm text-slate-900">Ask AI</div>
          <div className="text-[11px] text-slate-500">natural-language queries · results cite each contract</div>
          <button onClick={onClose} className="ml-auto text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={e => { e.preventDefault(); run(q); }} className="p-4 border-b border-slate-100">
          <input value={q} onChange={e => setQ(e.target.value)} autoFocus
                 data-testid="ai-search-input"
                 placeholder='e.g., "all McKesson contracts" or "contracts over $1M" or "expiring next quarter"'
                 className="w-full h-10 px-3 text-sm border border-slate-200 rounded focus:border-[#005289]" />
          <button type="submit" disabled={busy}
                  data-testid="ai-search-submit"
                  className="mt-2 h-9 px-4 rounded text-xs font-semibold bg-[#005289] text-white disabled:opacity-40">
            {busy ? "Thinking…" : "Ask"}
          </button>
        </form>
        <div className="flex-1 overflow-auto p-5">
          {busy && <div className="text-sm text-slate-500 text-center py-6">Searching + generating answer…</div>}
          {data && (
            <div className="space-y-4">
              <div className="text-sm text-slate-800 whitespace-pre-wrap"
                   data-testid="ai-search-answer">{data.answer}</div>
              {data.match_count > 0 && (
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                  {data.match_count} contract{data.match_count === 1 ? "" : "s"} match — showing {data.citations?.length || 0} citations
                  {data.amount_filter ? ` · amount ≥ $${data.amount_filter.toLocaleString()}` : ""}
                </div>
              )}
              <div className="space-y-2">
                {(data.citations || []).map(c => (
                  <button key={c.contract_id} onClick={() => { nav(`/contract/${c.contract_id}`); onClose(); }}
                          data-testid={`ai-citation-${c.contract_number}`}
                          className="w-full text-left border border-slate-200 rounded p-3 hover:border-[#005289] hover:bg-slate-50">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-mono font-bold text-slate-700">#{c.contract_number}</span>
                      <span className="font-semibold text-slate-900">{c.vendor}</span>
                      <span className="text-slate-500">· {c.department}</span>
                      <span className="ml-auto text-slate-400"><ExternalLink className="w-3 h-3" /></span>
                    </div>
                    {c.snippet && <div className="text-[11px] text-slate-600 mt-1 italic">"{c.snippet}"</div>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
